/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import type { IChatThread, IToolCallData } from "@/types/data-models";
import { ThinkingTimeline } from "./thinking-timeline";
import {
  Bot,
  ChevronsDown,
  Loader2,
  Plus,
  Send,
} from "lucide-react";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { aiApi } from "@/api/ai-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { isSubscriptionLimitError, useAiApi } from "@/hooks/use-ai-api";
import { useStreamingTypewriter } from "@/hooks/use-streaming-typewriter";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { useWorkspaceStore } from "@/store/workspace-store";
import { Streamdown } from "streamdown";

const EXAMPLE_QUESTIONS = [
  "What is the company's business model?",
  "Analyze their market position",
  "What are the key risks?",
  "Who are their competitors?",
  "Revenue growth analysis",
  "Team background check",
  "Product differentiation",
  "Customer acquisition strategy",
  "Unit economics breakdown",
  "Market size estimation",
  "Regulatory compliance",
  "Technology stack review",
  "Funding history",
  "Exit opportunities",
  "Valuation analysis",
  "IP portfolio strength",
  "Customer retention metrics",
  "Supply chain assessment",
  "Growth opportunities",
  "Financial health check",
];

export function DiligencegptSidebar({
  currentDeal,
  ...props
}: React.ComponentProps<typeof Sidebar> & { currentDeal: DealTransformed }) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const { handleApiCall } = useAiApi();
  const activeWorkspaceId = useWorkspaceStore(
    (state) => state.activeWorkspaceId
  );

  // Thread management state
  const [threads, setThreads] = React.useState<IChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = React.useState<string | null>(
    null
  );
  const [currentThread, setCurrentThread] = React.useState<IChatThread | null>(
    null
  );
  const [streamingContent, setStreamingContent] = React.useState("");
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [toolCalls, setToolCalls] = React.useState<IToolCallData[]>([]);
  // Streaming tool reasoning content (shown in muted style during "Thinking")
  const [toolReasoningContent, setToolReasoningContent] = React.useState("");
  const toolReasoningContentRef = React.useRef(""); // Ref for callback access
  const [currentToolName, setCurrentToolName] = React.useState<string | null>(null);
  // Track if we just finished streaming to prevent clearing tool calls too early
  const justFinishedStreamingRef = React.useRef(false);

  const streamedAIContentRef = React.useRef("");

  // Unified sidebar loading
  const [isSidebarLoading, setIsSidebarLoading] = React.useState(true);
  const [didThreadsLoad, setDidThreadsLoad] = React.useState(false);
  const [didCurrentThreadLoad, setDidCurrentThreadLoad] = React.useState(false);

  // Add state and ref
  const [streamFinished, setStreamFinished] = React.useState(false);

  // Model picker - UI hidden per SAAS-498, hardcoded default model
  const selectedModel = "gpt-4.1";

  // Use typewriter effect for smooth character-by-character animation
  // This creates a smooth typing effect that catches up to streaming content
  const displayedContent = useStreamingTypewriter({
    content: streamingContent,
    speed: 15, // Slower speed for readable streaming (15ms per character)
    isActive: isStreaming,
  });

  // Format tool names for display
  const formatToolName = (toolName: string): string => {
    const toolNameMap: Record<string, string> = {
      find_context_by_query: "Find Context From Dataroom",
      rag_tool: "RAG Search",
      excel_sheet_tool: "Excel Analysis",
    };

    if (toolNameMap[toolName]) {
      return toolNameMap[toolName];
    }

    // Generic conversion: snake_case → Title Case
    return toolName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Check if we're in the "analysing" phase (streaming but no content yet)
  // Show indicator when streaming starts but before any content is displayed
  const isAnalysing = isStreaming && displayedContent.trim().length === 0;

  // Check if we should show the tool reasoning section (during and after streaming with tool content)
  // Persist tool reasoning until a new message is sent (when toolReasoningContent is cleared)
  const shouldShowToolReasoning = toolReasoningContent.length > 0;



  // Adjust useEffect for threads
  React.useEffect(() => {
    const loadThreads = async () => {
      setIsSidebarLoading(true);
      setDidThreadsLoad(false);
      if (!currentDeal?.id || !activeWorkspaceId) {
        setThreads([]);
        setDidThreadsLoad(true);
        setDidCurrentThreadLoad(true); // No threads/chats to load
        setIsSidebarLoading(false);
        return;
      }
      try {
        const response = await handleApiCall(() =>
          aiApi.getChatThreads({
            companyId: currentDeal.id,
            opportunityId: activeWorkspaceId,
          })
        );
        if (response.success && response.threads) {
          if (response.threads.length > 0) {
            setThreads(response.threads);
            if (!activeThreadId) {
              setActiveThreadId(response.threads[0]._id);
            }
          } else {
            // Auto-create first thread if none exist
            const createResponse = await handleApiCall(() =>
              aiApi.createChatThread({
                companyId: currentDeal.id,
                opportunityId: activeWorkspaceId,
              })
            );
            if (createResponse.success && createResponse.threadId) {
              const newThread: IChatThread = {
                _id: createResponse.threadId,
                messages: [],
                opportunity_application_id: "",
                updated_on: new Date().toISOString(),
              };
              setThreads([newThread]);
              setActiveThreadId(createResponse.threadId);
              setCurrentThread(newThread);
            }
          }
        } else {
          setThreads([]);
        }
      } catch {
        setThreads([]);
      } finally {
        setDidThreadsLoad(true);
      }
    };
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeal?.id, activeWorkspaceId]);

  // Extract tool calls from messages_history when thread loads
  React.useEffect(() => {
    if (currentThread?.messages_history) {
      const toolCallNamesFromHistory: string[] = [];
      const messages = currentThread.messages_history;

      // Extract tool calls from history
      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        if (msg.node === "tool" && msg.name) {
          // Only add if not already in the list (avoid duplicates)
          if (!toolCallNamesFromHistory.includes(msg.name)) {
            toolCallNamesFromHistory.push(msg.name);
          }
        }
      }

      // Only update tool calls from history if we're not currently streaming
      // This prevents clearing tool calls during active streaming
      if (!isStreaming) {
        // Extract tool calls for the LAST AI message in history
        // This ensures we show tool calls for the most recent response
        let lastAIMessageIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].node === "ai") {
            lastAIMessageIndex = i;
            break;
          }
        }
        if (lastAIMessageIndex !== -1) {
          // Look backwards from the last AI message to find tool calls with their content
          const toolCallDataForLastAI: IToolCallData[] = [];
          for (let j = lastAIMessageIndex - 1; j >= 0; j--) {
            const prevMsg = messages[j];
            if (prevMsg.node === "tool" && prevMsg.name) {
              if (!toolCallDataForLastAI.some(t => t.name === prevMsg.name)) {
                toolCallDataForLastAI.unshift({
                  name: prevMsg.name,
                  result: prevMsg.content || "", // Extract tool output content!
                  status: "completed"
                });
              }
            } else if (prevMsg.node === "human") {
              break;
            }
          }
          // Set tool calls with full data if found in history
          if (toolCallDataForLastAI.length > 0) {
            setToolCalls(toolCallDataForLastAI);
            justFinishedStreamingRef.current = false; // Tool calls are now in history
          } else if (justFinishedStreamingRef.current) {
            // Just finished streaming, keep tool calls from state (not in history yet)
            // Don't clear them - they'll be persisted by backend soon
          }
          // DON'T clear tool calls if we have them in state but not in history yet
          // This happens when streaming just finished and backend hasn't persisted them yet
          // Only clear if we have messages but no tool calls in state AND not in history
          // (This check is handled by the else-if below)
        } else if (messages.length > 0) {
          // No AI messages yet, only clear if we don't have tool calls in state
          // (might be from a previous message that's no longer relevant)
          if (toolCalls.length === 0) {
            setToolCalls([]);
          }
        }
      }
      // If streaming, keep the current toolCalls state (don't overwrite)
    } else if (!currentThread && !isStreaming) {
      // Clear tool calls when thread is cleared and not streaming
      setToolCalls([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThread?.messages_history, currentThread?._id]);

  // Adjust useEffect for active thread
  React.useEffect(() => {
    const loadThread = async () => {
      if (!activeThreadId) {
        setCurrentThread(null);
        setDidCurrentThreadLoad(true);
        setIsSidebarLoading(false);
        return;
      }
      setDidCurrentThreadLoad(false);
      try {
        const response = await handleApiCall(() =>
          aiApi.getChatThread(activeThreadId)
        );
        if (response.success && response.thread) {
          setCurrentThread(response.thread);
          scrollToBottom();
        } else {
          setCurrentThread(null);
        }
      } catch {
        setCurrentThread(null);
      } finally {
        setDidCurrentThreadLoad(true);
      }
    };
    if (didThreadsLoad) {
      loadThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, didThreadsLoad]);

  // isSidebarLoading: loader until both didThreadsLoad and didCurrentThreadLoad
  React.useEffect(() => {
    // If there are no threads, only thread list has to finish
    if (didThreadsLoad && (threads.length === 0 || didCurrentThreadLoad)) {
      setIsSidebarLoading(false);
    } else {
      setIsSidebarLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didThreadsLoad, didCurrentThreadLoad, threads.length]);

  // Model fetching disabled - UI hidden per SAAS-498
  // To re-enable: uncomment useEffect and add back availableModels/isLoadingModels state

  // Add an interval ref for continuous scrolling
  const scrollIntervalRef = React.useRef<NodeJS.Timer | null>(null);

  // Whether the chat auto-follows the bottom. The user scrolling up unsticks
  // it (so they can read earlier messages while a response streams); scrolling
  // back to the bottom, sending a message, or switching threads re-sticks it.
  const stickToBottomRef = React.useRef(true);

  // Re-engage auto-follow on thread switch so opening a thread lands at its end.
  React.useEffect(() => {
    stickToBottomRef.current = true;
  }, [activeThreadId]);

  // Add useEffect to scroll to bottom on initial load
  React.useEffect(() => {
    scrollToBottom();
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom after history loads
  React.useEffect(() => {
    if (
      !isSidebarLoading &&
      currentThread?.messages_history?.length &&
      currentThread.messages_history.length > 0 &&
      stickToBottomRef.current
    ) {
      scrollToBottom();
    }
  }, [isSidebarLoading, currentThread?.messages_history?.length]);

  // Scroll to bottom as chat streams
  React.useEffect(() => {
    if (isStreaming && stickToBottomRef.current) {
      scrollToBottom();
    }
  }, [streamingContent, isStreaming]);

  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isNotAtBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight > 20;
    // Scrolling away from the bottom unsticks auto-follow; returning re-sticks.
    stickToBottomRef.current = !isNotAtBottom;
    setShowScrollButton(isNotAtBottom);
  }, []);

  // Update scrollToBottom to be more aggressive
  const scrollToBottom = React.useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "auto",
      });
    }
  }, []);

  // Start continuous scrolling during typing
  const startScrolling = React.useCallback(() => {
    if (scrollIntervalRef.current) return;
    scrollIntervalRef.current = setInterval(() => {
      if (stickToBottomRef.current) scrollToBottom();
    }, 10);
  }, [scrollToBottom]);

  // Stop continuous scrolling
  const stopScrolling = React.useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current as NodeJS.Timeout);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopScrolling();
    };
  }, [stopScrolling]);

  // Create new thread
  const handleCreateThread = async () => {
    if (!currentDeal?.id || !activeWorkspaceId) {
      toast.error("Unable to create thread: Missing company or opportunity ID");
      return;
    }

    try {
      const response = await handleApiCall(() =>
        aiApi.createChatThread({
          companyId: currentDeal.id,
          opportunityId: activeWorkspaceId,
        })
      );
      if (response.success && response.threadId) {
        const newThread: IChatThread = {
          _id: response.threadId,
          messages: [],
          opportunity_application_id: "",
          updated_on: new Date().toISOString(),
        };
        setThreads([...threads, newThread]);
        setActiveThreadId(response.threadId);
        setCurrentThread(newThread);
        toast.success("New thread created");
      }
    } catch (error) {
      console.error("Failed to create thread:", error);
      if (!isSubscriptionLimitError(error)) {
        toast.error("Failed to create thread");
      }
    }
  };

  const handleSendMessage = async () => {
    if (
      !inputValue.trim() ||
      isLoading ||
      !activeThreadId ||
      !currentDeal?.id ||
      !activeWorkspaceId
    ) {
      if (!activeThreadId) {
        toast.error("Please select or create a thread first");
      }
      return;
    }
    const userMessage = inputValue.trim();
    setInputValue("");
    setIsLoading(true);
    setIsStreaming(true);
    setStreamFinished(false);
    setStreamingContent("");
    setToolCalls([]); // Reset tool calls for new message
    setToolReasoningContent(""); // Reset tool reasoning content
    toolReasoningContentRef.current = ""; // Reset tool reasoning ref
    setCurrentToolName(null); // Reset current tool name
    justFinishedStreamingRef.current = false; // Reset streaming flag
    streamedAIContentRef.current = ""; // Instead of setStreamedAIContent

    // Immediately append human message to messages_history (UX instant)
    setCurrentThread((prev) => {
      if (!prev) return prev;
      const nextId = `user-${Date.now()}`;
      return {
        ...prev,
        messages_history: [
          ...(prev.messages_history || []),
          { node: "human", content: userMessage, id: nextId },
        ],
        // Optionally also add to messages if you need, but not required now
      };
    });

    // The user just sent a message — always follow it and the incoming reply.
    stickToBottomRef.current = true;
    scrollToBottom();
    startScrolling();

    try {
      await handleApiCall(async () => {
        return aiApi.sendChatMessageStream({
          chatId: activeThreadId,
          userQuery: userMessage,
          companyId: currentDeal.id,
          opportunityId: activeWorkspaceId,
          model: selectedModel || undefined,
          onChunk: (chunk: string) => {
            setStreamingContent((prev) => {
              const newContent = prev + chunk;
              return newContent;
            });
            streamedAIContentRef.current += chunk; // Use .current instead of setState!
            if (stickToBottomRef.current) scrollToBottom();
          },
          onToolCall: (toolData: { name: string; content?: unknown;[key: string]: unknown }) => {
            const content = typeof toolData.content === 'string' ? toolData.content : '';
            const toolName = toolData.name;

            console.log('[SIDEBAR] onToolCall received:', { toolName, content: content.substring(0, 50), fullData: toolData });

            // If we have a real tool name (final chunk), set it and finalize the tool
            if (toolName && toolName !== 'tool') {
              console.log('[SIDEBAR] Final chunk with tool name:', toolName);
              setCurrentToolName(toolName);
              // Add to tool calls list for history (use ref for accumulated content)
              setToolCalls((prev) => {
                if (!prev.some(t => t.name === toolName)) {
                  return [...prev, {
                    name: toolName,
                    status: "completed",
                    result: toolReasoningContentRef.current || content
                  } as IToolCallData];
                }
                return prev;
              });
            } else if (content) {
              // No name yet, just streaming content - accumulate as reasoning
              console.log('[SIDEBAR] Accumulating tool content, length:', content.length);
              toolReasoningContentRef.current += content;
              setToolReasoningContent(toolReasoningContentRef.current);
            }
            if (stickToBottomRef.current) scrollToBottom();
          },
          onComplete: async () => {
            // CRITICAL: Set streaming to false FIRST to prevent duplicate rendering
            setIsStreaming(false);
            setIsLoading(false);
            setStreamFinished(true);
            stopScrolling();

            // Mark that we just finished streaming to prevent clearing tool calls
            justFinishedStreamingRef.current = true;

            // DON'T clear tool calls here - keep them visible until thread reloads
            // The tool calls will be extracted from history when the thread is reloaded

            // Append the AI message as soon as done streaming (use robust state!)
            // Skip empty AI messages
            if (streamedAIContentRef.current.trim()) {
              const newMessageId = `ai-${Date.now()}`;

              // Use a ref to track if we've already added this message to prevent duplicates
              const messageContent = streamedAIContentRef.current;

              setCurrentThread((prev) => {
                if (!prev) {
                  return prev;
                }
                const existingIds = (prev.messages_history || []).map(
                  (m) => m.id
                );

                // Check if this exact message was already added (by content, not just ID)
                const lastMessage =
                  prev.messages_history?.[prev.messages_history.length - 1];
                if (
                  lastMessage?.node === "ai" &&
                  lastMessage?.content === messageContent
                ) {
                  return prev; // Don't add duplicate
                }

                if (existingIds.includes(newMessageId)) {
                  // Generate a new ID if duplicate
                  const finalMessageId = `ai-${Date.now()}-${Math.random()
                    .toString(36)
                    .substring(2, 9)}`;
                  return {
                    ...prev,
                    messages_history: [
                      ...(prev.messages_history || []),
                      {
                        node: "ai" as const,
                        content: messageContent,
                        id: finalMessageId,
                        name: null,
                      },
                    ],
                  };
                }

                const newThread = {
                  ...prev,
                  messages_history: [
                    ...(prev.messages_history || []),
                    {
                      node: "ai" as const,
                      content: messageContent,
                      id: newMessageId,
                      name: null,
                    },
                  ],
                };
                return newThread;
              });

              // Clear streaming content after adding to history
              setStreamingContent("");
              // Don't clear tool calls - keep them visible after completion
              streamedAIContentRef.current = "";
            } else {
              console.error(`[SIDEBAR] onComplete SKIPPED - empty content`);
            }
          },
          onError: (error: Error) => {
            setIsStreaming(false);
            setStreamingContent("");
            // Don't clear tool calls on error - keep them visible
            streamedAIContentRef.current = "";
            stopScrolling();
            console.error("Streaming error:", error);
            if (!isSubscriptionLimitError(error)) {
              toast.error("Failed to send message. Please try again.");
            }
          },
        });
      });
    } catch (error) {
      setIsStreaming(false);
      setStreamingContent("");
      // Don't clear tool calls on error - keep them visible
      streamedAIContentRef.current = "";
      stopScrolling();
      console.error("Failed to send message:", error);
      if (!isSubscriptionLimitError(error)) {
        toast.error("Failed to send message. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationFrames = useRef<(number | null)[]>([null, null, null]);
  const positionsRef = useRef<number[]>([0, 0, 0]);
  const pausedRowsRef = useRef<boolean[]>([false, false, false]);
  const [pausedRows, setPausedRows] = useState<boolean[]>([
    false,
    false,
    false,
  ]);

  // Split questions into 3 rows
  const rows = [
    EXAMPLE_QUESTIONS.slice(0, 7),
    EXAMPLE_QUESTIONS.slice(7, 14),
    EXAMPLE_QUESTIONS.slice(14, 20),
  ];

  // Keep ref in sync with state so the animation loop reads current pause state
  useEffect(() => {
    pausedRowsRef.current = pausedRows;
  }, [pausedRows]);

  useEffect(() => {
    // Initialize refs array
    rowRefs.current = rowRefs.current.slice(0, rows.length);

    // Function to animate a single row
    const animateRow = (
      rowElement: HTMLDivElement,
      speed: number,
      rowIndex: number
    ) => {
      const animate = () => {
        if (!pausedRowsRef.current[rowIndex]) {
          positionsRef.current[rowIndex] += speed;
          const maxScroll = rowElement.scrollWidth / 2;

          if (positionsRef.current[rowIndex] >= maxScroll) {
            positionsRef.current[rowIndex] = 0;
          }

          rowElement.scrollLeft = positionsRef.current[rowIndex];
        }
        animationFrames.current[rowIndex] = requestAnimationFrame(animate);
      };

      animationFrames.current[rowIndex] = requestAnimationFrame(animate);
    };

    // Start animation for each row with slower speeds
    rowRefs.current.forEach((row, index) => {
      if (row) {
        animateRow(row, 0.3 + index * 0.05, index); // Slower base speed and smaller differences
      }
    });

    // Cleanup
    return () => {
      animationFrames.current.forEach((frame, index) => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
          animationFrames.current[index] = null;
        }
      });
    };
    // Only re-run when rows change, not on pause state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  return (
    <Sidebar
      collapsible="icon"
      side="right"
      {...props}
      data-tour="diligencegpt-chat"
    >
      <SidebarHeader className="flex flex-row items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 shrink-0">
          {/* Thread dropdown spinner */}
          {threads.length > 0 ? (
            <>
              <Select
                value={activeThreadId || ""}
                onValueChange={(value) => {
                  setActiveThreadId(value);
                  setStreamingContent("");
                  setIsStreaming(false);
                }}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs">
                  <SelectValue placeholder="Select thread" />
                </SelectTrigger>
                <SelectContent>
                  {threads.map((thread) => (
                    <SelectItem key={thread._id} value={thread._id}>
                      {thread.thread_name && thread.thread_name !== "New chat"
                        ? thread.thread_name
                        : new Date(thread.updated_on).toLocaleDateString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                onClick={handleCreateThread}
                title="New Thread"
                className="h-8 w-8"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={handleCreateThread}
              title="New Thread"
              className="h-8 w-8"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent
        ref={contentRef}
        onScroll={handleScroll}
        className="flex flex-col gap-4 p-4 relative"
      >
        {isSidebarLoading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-2 w-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Loading chat…</span>
          </div>
        ) : (
          <>
            {!isSidebarLoading && threads.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full py-16">
                <div className="mb-4 text-base font-medium text-muted-foreground">
                  No conversations yet.
                </div>
                <Button type="button" size="lg" onClick={handleCreateThread}>
                  <Plus className="h-4 w-4 mr-2" /> Start Chatting
                </Button>
              </div>
            )}
            {threads.length > 0 && (
              <>
                {(() => {
                  const filteredMessages =
                    currentThread?.messages_history?.filter((msg) => {
                      // Skip tool result messages (these contain RAG/Excel output, not for display)
                      if (msg.node === "tool") {
                        return false;
                      }

                      // Skip empty AI messages (tool invocation phase)
                      if (msg.node === "ai" && !msg.content?.trim()) {
                        return false;
                      }

                      return true;
                    }) || [];

                  // Show empty state when thread exists but has no messages
                  if (
                    filteredMessages.length === 0 &&
                    !isStreaming &&
                    !streamingContent
                  ) {
                    return (
                      <div className="flex flex-col items-center justify-center h-full py-16 px-4">
                        <div className="flex h-12 w-12 mb-4 shrink-0 items-center justify-center rounded-lg bg-background border border-border">
                          <Bot className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div className="mb-2 text-sm font-medium text-muted-foreground text-center max-w-md">
                          Ask questions about the company our agents will work
                          to get you the most relevant answers
                        </div>
                      </div>
                    );
                  }

                  return filteredMessages.map((msg, i) => {
                    // Find tool calls that appear before this AI message in the full history
                    let toolCallsForThisMessage: IToolCallData[] = [];
                    if (msg.node === "ai" && currentThread?.messages_history) {
                      const fullHistory = currentThread.messages_history;
                      const currentMsgIndex = fullHistory.findIndex(
                        (m) => m.id === msg.id
                      );
                      if (currentMsgIndex !== -1) {
                        // Look backwards from this AI message to find tool calls with their content
                        const toolCallsFound: IToolCallData[] = [];
                        for (let j = currentMsgIndex - 1; j >= 0; j--) {
                          const prevMsg = fullHistory[j];
                          if (prevMsg.node === "tool" && prevMsg.name) {
                            if (!toolCallsFound.some(t => t.name === prevMsg.name)) {
                              toolCallsFound.unshift({
                                name: prevMsg.name,
                                result: prevMsg.content || "", // Extract tool output content!
                                status: "completed"
                              }); // Add to beginning to maintain order
                            }
                          } else if (prevMsg.node === "human") {
                            // Stop when we hit a human message (tool calls belong to the AI response after human)
                            break;
                          }
                        }
                        toolCallsForThisMessage = toolCallsFound;

                        // If this is the last message and we have tool calls in state but not in history,
                        // use the state tool calls (they might not be persisted yet)
                        if (
                          i === filteredMessages.length - 1 &&
                          toolCallsFound.length === 0 &&
                          toolCalls.length > 0
                        ) {
                          toolCallsForThisMessage = toolCalls;
                        }
                      }
                    }

                    return (
                      <div
                        key={msg.id || `msg-history-${i}`}
                        className="flex flex-col gap-2"
                      >
                        {msg.node === "human" ? (
                          <div className="flex gap-2 justify-end mb-2">
                            <div className="rounded-2xl px-4 py-2.5 text-[14px] bg-foreground text-background max-w-[85%] leading-relaxed font-medium">
                              {msg.content}
                            </div>
                          </div>
                        ) : msg.node === "ai" ? (
                          <div className="flex gap-3 flex-row mb-6">
                            <div className="flex h-7 w-7 mt-0.5 shrink-0 items-center justify-center rounded-sm bg-background border border-border">
                              <Bot className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0 text-[14px] text-foreground transition-colors max-w-none pt-0.5">
                              {/* Collapsible thinking timeline for tool calls */}
                              {toolCallsForThisMessage.length > 0 && (
                                <ThinkingTimeline
                                  toolCalls={toolCallsForThisMessage}
                                  formatToolName={formatToolName}
                                />
                              )}
                              <Streamdown
                                parseIncompleteMarkdown={true}
                                className="prose prose-sm dark:prose-invert max-w-none leading-relaxed font-normal"
                              >
                                {msg.content || ""}
                              </Streamdown>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  });
                })()}
                {/* Show thinking timeline when streaming starts but before content arrives */}
                {isAnalysing && (
                  <div className="flex gap-3 flex-row mb-4">
                    <div className="flex h-7 w-7 mt-0.5 shrink-0 items-center justify-center rounded-sm bg-background border border-border">
                      <Bot className="h-4 w-4 text-muted-foreground animate-pulse" />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5 pb-2 text-foreground">
                      <ThinkingTimeline
                        toolCalls={toolCalls}
                        reasoningContent={toolReasoningContent}
                        currentToolName={currentToolName}
                        isStreaming={isStreaming}
                        defaultExpanded={true}
                        formatToolName={formatToolName}
                      />
                    </div>
                  </div>
                )}
                {/* Only show AI streaming bubble while actually streaming AND has content */}
                {(() => {
                  // Only show if actively streaming AND has content
                  // Don't show if streamFinished is true (message already added to history)
                  const shouldShowStreaming =
                    isStreaming && streamingContent && !streamFinished;
                  return shouldShowStreaming;
                })() && (
                    <div className="flex gap-3 flex-row mb-6">
                      <div className="flex h-7 w-7 mt-0.5 shrink-0 items-center justify-center rounded-sm bg-background border border-border">
                        <Bot className="h-4 w-4 text-muted-foreground animate-pulse" />
                      </div>
                      <div className="flex-1 min-w-0 text-[14px] text-foreground transition-colors max-w-none pt-0.5">
                        {/* Collapsible thinking timeline during streaming */}
                        {(toolCalls.length > 0 || shouldShowToolReasoning) && (
                          <ThinkingTimeline
                            toolCalls={toolCalls}
                            reasoningContent={toolReasoningContent}
                            currentToolName={currentToolName}
                            isStreaming={isStreaming}
                            defaultExpanded={true}
                            formatToolName={formatToolName}
                          />
                        )}
                        <Streamdown
                          isAnimating={isStreaming}
                          parseIncompleteMarkdown={true}
                          className="prose prose-sm dark:prose-invert max-w-none leading-relaxed font-normal"
                        >
                          {displayedContent}
                        </Streamdown>
                        {isStreaming && <span className="animate-pulse ml-1 text-primary">▋</span>}
                      </div>
                    </div>
                  )}

                {showScrollButton && (
                  <Button
                    size="icon"
                    variant="secondary"
                    className="fixed bottom-36 right-8 rounded-full shadow-md z-10"
                    onClick={() => {
                      stickToBottomRef.current = true;
                      scrollToBottom();
                    }}
                  >
                    <ChevronsDown className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="bg-transparent mb-2">
        <div className="relative px-3 pb-2 pt-0 w-full flex flex-col items-center">
          {/* Example Questions Carousel - Show all 3 rows when empty, only 1 row when conversation started */}
          {(() => {
            const hasMessages = (currentThread?.messages_history?.filter(m => m.node === "human" || m.node === "ai")?.length ?? 0) > 0;
            const rowsToShow = hasMessages ? rows.slice(0, 1) : rows; // Show 1 row if has messages, all 3 if empty
            return (
              <div className="w-full overflow-hidden mb-3">
                {rowsToShow.map((row, rowIndex) => (
                  <section
                    key={`row-${rowIndex}-${row[0]?.substring(0, 20) ?? ""}-${row.length
                      }`}
                    className="overflow-hidden mb-2 last:mb-0"
                    aria-label={`Question row ${rowIndex + 1}`}
                    onMouseEnter={() => {
                      setPausedRows((prev) => {
                        const newState = [...prev];
                        newState[rowIndex] = true;
                        return newState;
                      });
                    }}
                    onMouseLeave={() => {
                      setPausedRows((prev) => {
                        const newState = [...prev];
                        newState[rowIndex] = false;
                        return newState;
                      });
                    }}
                  >
                    <div
                      ref={(el) => {
                        if (el) {
                          rowRefs.current[rowIndex] = el;
                        }
                      }}
                      className="flex gap-2 py-1 whitespace-nowrap overflow-hidden"
                    >
                      {/* Triple the content for smooth infinite scroll */}
                      {[...row, ...row, ...row].map((question, index) => (
                        <button
                          type="button"
                          key={`question-${rowIndex}-${index}-${question.substring(
                            0,
                            10
                          )}`}
                          onClick={() => {
                            setInputValue(question);
                            contentRef.current?.scrollTo({
                              top: contentRef.current.scrollHeight,
                              behavior: "smooth",
                            });
                          }}
                          className="text-[11px] px-3 py-1.5 rounded-full bg-muted hover:bg-foreground/[0.08] text-muted-foreground hover:text-foreground whitespace-nowrap transition-all border border-transparent hover:border-foreground/20"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            );
          })()}

          <div className="w-full flex justify-center sticky bottom-0">
            <div className="flex flex-col w-full max-w-2xl bg-background border border-border rounded-2xl shadow-sm overflow-hidden transition-all focus-within:ring-1 focus-within:ring-foreground/20">
              {/* Chat Input */}
              <div className="flex items-end gap-2 p-2">
                <Input
                  autoFocus
                  placeholder="Message AI..."
                  className="flex-1 min-h-[44px] bg-transparent border-0 text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-3 resize-none shadow-none text-[14px]"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={
                    isSidebarLoading ||
                    isLoading ||
                    !activeThreadId ||
                    threads.length === 0
                  }
                />

                <Button
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl mb-0.5 mr-0.5 bg-primary hover:bg-primary/90 transition-colors disabled:opacity-50"
                  onClick={handleSendMessage}
                  disabled={
                    isSidebarLoading ||
                    isLoading ||
                    !activeThreadId ||
                    threads.length === 0 ||
                    (!inputValue.trim() && !isStreaming)
                  }
                >
                  {isLoading || isStreaming ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-foreground" />
                  ) : (
                    <Send className="h-4 w-4 text-primary-foreground" />
                  )}
                </Button>
              </div>

              {/* Footer Actions - Model picker hidden per SAAS-498 */}
              <div className="flex items-center justify-center px-3 pb-2 pt-0 w-full">
                <span className="text-[10px] text-muted-foreground">
                  AI responses are for reference only
                </span>
              </div>
            </div>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
